import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { api } from "../lib/api";
import { MODULES, moduleFromPath, tourFor } from "../lib/tours";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CircleHelp, PlayCircle, Footprints, VideoOff } from "lucide-react";

// Convierte enlaces de YouTube / Vimeo / Drive a versión embebible; otros se usan tal cual
export function embedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  const gd = url.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  return url;
}

let helpCache = null;
export default function HelpButton() {
  const { pathname } = useLocation();
  const module = moduleFromPath(pathname);
  const name = (MODULES.find((m) => m[0] === module) || [module, module])[1];
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(helpCache || {});
  useEffect(() => { if (!helpCache) api.get("/help").then((r) => { helpCache = r.data; setHelp(r.data); }).catch(() => {}); }, []);
  const info = help[module] || {};
  const video = embedUrl(info.video_url);

  const startTour = () => {
    setOpen(false);
    const steps = tourFor(module).filter((s) => document.querySelector(s.el)).map((s) => ({ element: s.el, popover: { title: s.title, description: s.text } }));
    if (!steps.length) return;
    setTimeout(() => driver({ showProgress: true, animate: true, allowClose: true, nextBtnText: "Siguiente", prevBtnText: "Anterior", doneBtnText: "Listo", progressText: "{{current}} de {{total}}", steps }).drive(), 250);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="h-8 px-3 rounded-full inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200" title={`Ayuda · ${name}`} data-testid="help-button">
        <CircleHelp size={15} /> Ayuda
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={video ? "max-w-3xl" : "max-w-md"} data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><CircleHelp size={18} className="text-emerald-600" /> Ayuda · {info.title || name}</DialogTitle>
            <DialogDescription>{info.notes || "Mira el video explicativo o inicia el recorrido guiado por la pantalla."}</DialogDescription>
          </DialogHeader>
          {video ? (
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-black" data-testid="help-video">
              <iframe src={video} title={`Video ${name}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="help-no-video"><VideoOff size={22} /> Aún no hay video para este módulo.</div>
          )}
          <div className="flex gap-2">
            {video && <Button variant="outline" className="flex-1" onClick={() => window.open(info.video_url, "_blank")} data-testid="help-open-video"><PlayCircle size={16} className="mr-1" /> Abrir video</Button>}
            <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold" onClick={startTour} data-testid="help-start-tour"><Footprints size={16} className="mr-1" /> Iniciar recorrido guiado</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
